((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaStoreMemory = api;
  if (root?.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', () => api.init(root.document, root));
    } else {
      api.init(root.document, root);
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : window, () => {
  'use strict';

  const STORAGE_KEY = 'corkopia-store-memory-v1';
  const HANDOFF_KEY = 'corkopia-first-run-handoff-v1';
  const BUILD_KEY = 'corkopia-build-studio-v1';
  const SMART_BOX_KEY = 'corkopia-smart-box-advisor-v1';
  const SEARCH_HISTORY_KEY = 'corkopia-search-history-v1';
  const VERSION = 1;
  const MAX_RECENT_SEARCHES = 5;
  const MAX_RECENT_PROJECTS = 4;
  const MAX_RECENT_FAMILIES = 6;
  const FAMILIES = Object.freeze([
    'cork_round',
    'half_round',
    'cork_flat',
    'branch',
    'background_piece',
    'statement_piece'
  ]);

  const EXPERIENCES = Object.freeze({
    new_habitat: Object.freeze({
      kind: 'pathway',
      destination: 'build',
      label: 'New Habitat',
      contextLabel: 'new habitat',
      searchPrompt: 'Try “a branching piece for a new habitat”',
      handoff: Object.freeze({ mode: 'new_habitat' })
    }),
    existing_habitat: Object.freeze({
      kind: 'pathway',
      destination: 'build',
      label: 'Existing Habitat',
      contextLabel: 'existing habitat',
      searchPrompt: 'Try “a focal piece for an existing habitat”',
      handoff: Object.freeze({ mode: 'existing_habitat' })
    }),
    orchid_project: Object.freeze({
      kind: 'pathway',
      destination: 'build',
      label: 'Orchid Project',
      contextLabel: 'orchid project',
      searchPrompt: 'Try “a flat or branch for an orchid mount”',
      handoff: Object.freeze({ mode: 'orchid_project', project: 'orchid_mount' })
    }),
    just_browsing: Object.freeze({
      kind: 'pathway',
      destination: 'browse',
      label: 'Just Browsing',
      contextLabel: 'open exploration',
      searchPrompt: 'Describe a shape, measurement, family, or budget',
      handoff: Object.freeze({ mode: 'just_browsing' })
    }),
    continue_build: Object.freeze({
      kind: 'pathway',
      destination: 'build',
      label: 'Continue My Build',
      contextLabel: 'saved build',
      searchPrompt: 'Search for the next piece your build needs',
      handoff: Object.freeze({ mode: 'continue_build', resume: true })
    }),
    build_60: Object.freeze({
      kind: 'quick_start',
      destination: 'build',
      label: 'Build in 60 Seconds',
      contextLabel: 'quick guided build',
      searchPrompt: 'Describe one job the next piece should do',
      handoff: Object.freeze({ mode: 'build_60' })
    }),
    browse_everything: Object.freeze({
      kind: 'quick_start',
      destination: 'browse',
      label: 'Browse Everything',
      contextLabel: 'all current cork',
      searchPrompt: 'Describe a shape, measurement, family, or budget',
      handoff: Object.freeze({ mode: 'browse_everything' })
    }),
    popular_builds: Object.freeze({
      kind: 'quick_start',
      destination: 'popular',
      label: 'Popular Builds',
      contextLabel: 'popular starting directions',
      searchPrompt: 'Search current pieces by family or build role',
      handoff: Object.freeze({ mode: 'popular_builds' })
    }),
    start_empty: Object.freeze({
      kind: 'quick_start',
      destination: 'build',
      label: 'Start Empty',
      contextLabel: 'empty planning canvas',
      searchPrompt: 'Find the first piece for an empty build',
      handoff: Object.freeze({ mode: 'start_empty', reset: true })
    }),
    surprise_me: Object.freeze({
      kind: 'quick_start',
      destination: 'build',
      label: 'Surprise Me',
      contextLabel: 'open-ended build',
      searchPrompt: 'Show an unexpected currently available form',
      handoff: Object.freeze({ mode: 'surprise_me', style: 'surprise_me' })
    })
  });

  const text = (value, maximum = 160) => String(value || '').trim().slice(0, maximum);
  const timestamp = (value) => {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null;
  };
  const uniqueRecent = (items, identity, limit) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = identity(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, limit);
  };
  const parse = (storage, key, fallback = null) => {
    try {
      const value = JSON.parse(storage?.getItem(key) || 'null');
      return value === null ? fallback : value;
    } catch (_) {
      return fallback;
    }
  };
  const createMemory = (now = new Date().toISOString()) => ({
    version: VERSION,
    visitor: {
      firstSeenAt: now,
      lastSeenAt: now,
      visitCount: 0,
      welcomeDismissedAt: null
    },
    intent: {
      experienceId: null,
      selectedAt: null
    },
    preferences: {
      lastAnimal: null,
      lastBuild: null,
      lastSearch: null,
      preferredStyle: null,
      preferredBudget: null,
      recentFamilies: []
    },
    recentSearches: [],
    recentProjects: []
  });

  const sanitizeMemory = (input = {}, now = new Date().toISOString()) => {
    const fallback = createMemory(now);
    const visitor = input.visitor || {};
    const intent = input.intent || {};
    const preferences = input.preferences || {};
    const searches = Array.isArray(input.recentSearches) ? input.recentSearches : [];
    const projects = Array.isArray(input.recentProjects) ? input.recentProjects : [];
    const families = Array.isArray(preferences.recentFamilies) ? preferences.recentFamilies : [];
    return {
      ...fallback,
      visitor: {
        firstSeenAt: timestamp(visitor.firstSeenAt) || now,
        lastSeenAt: timestamp(visitor.lastSeenAt) || now,
        visitCount: Math.max(0, Number.parseInt(visitor.visitCount, 10) || 0),
        welcomeDismissedAt: timestamp(visitor.welcomeDismissedAt)
      },
      intent: {
        experienceId: Object.hasOwn(EXPERIENCES, intent.experienceId) ? intent.experienceId : null,
        selectedAt: timestamp(intent.selectedAt)
      },
      preferences: {
        lastAnimal: text(preferences.lastAnimal, 80) || null,
        lastBuild: text(preferences.lastBuild, 80) || null,
        lastSearch: text(preferences.lastSearch) || null,
        preferredStyle: text(preferences.preferredStyle, 80) || null,
        preferredBudget: text(preferences.preferredBudget, 40) || null,
        recentFamilies: uniqueRecent(
          families.map((family) => text(family, 80)).filter(Boolean),
          (family) => family,
          MAX_RECENT_FAMILIES
        )
      },
      recentSearches: uniqueRecent(searches.map((entry) => ({
        query: text(entry?.query),
        fingerprint: text(entry?.fingerprint),
        at: timestamp(entry?.at) || now
      })).filter((entry) => entry.query), (entry) => entry.query.toLowerCase(), MAX_RECENT_SEARCHES),
      recentProjects: uniqueRecent(projects.map((entry) => ({
        project: text(entry?.project, 80),
        style: text(entry?.style, 80) || null,
        budget: text(entry?.budget, 40) || null,
        step: Math.min(5, Math.max(1, Number(entry?.step) || 1)),
        at: timestamp(entry?.at) || now
      })).filter((entry) => entry.project), (entry) => entry.project, MAX_RECENT_PROJECTS)
    };
  };

  const read = (storage, now = new Date().toISOString()) => {
    const raw = parse(storage, STORAGE_KEY);
    const exists = Boolean(raw && raw.version === VERSION);
    return {
      exists,
      memory: sanitizeMemory(exists ? raw : {}, now)
    };
  };
  const write = (storage, memory) => {
    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify(sanitizeMemory(memory)));
      return true;
    } catch (_) {
      return false;
    }
  };
  const startVisit = (storage, now = new Date().toISOString()) => {
    const current = read(storage, now);
    current.memory.visitor.lastSeenAt = now;
    current.memory.visitor.visitCount += 1;
    write(storage, current.memory);
    return {
      firstVisit: !current.exists,
      returningVisitor: current.exists,
      memory: current.memory
    };
  };

  const buildHasProgress = (build = {}) => Boolean(
    build.project
    || Number(build.step) > 1
    || (Array.isArray(build.decisions) && build.decisions.length)
  );
  const canvasHasProgress = (build = {}) =>
    Object.keys(build.canvas?.placements || {}).length > 0;
  const inspectContinuity = (storage, cartCount = 0) => {
    const build = parse(storage, BUILD_KEY, {}) || {};
    const smartBox = parse(storage, SMART_BOX_KEY, {}) || {};
    const memory = read(storage).memory;
    return {
      savedBuild: buildHasProgress(build) && Boolean(timestamp(build.savedAt)),
      returningBuild: buildHasProgress(build),
      returningCanvas: canvasHasProgress(build),
      returningSmartBox: Number(cartCount) > 0 || Boolean(smartBox.savedAt),
      returningCart: Number(cartCount) > 0,
      buildStep: Math.min(5, Math.max(1, Number(build.step) || 1)),
      buildProject: text(build.project, 80) || memory.preferences.lastBuild,
      recentSearches: memory.recentSearches,
      recentProjects: memory.recentProjects
    };
  };

  const rememberIntent = (storage, experienceId, now = new Date().toISOString()) => {
    if (!Object.hasOwn(EXPERIENCES, experienceId)) return null;
    const memory = read(storage, now).memory;
    memory.intent = { experienceId, selectedAt: now };
    if (experienceId === 'orchid_project') {
      memory.preferences.lastBuild = 'orchid_mount';
    }
    write(storage, memory);
    return memory;
  };
  const dismissWelcome = (storage, now = new Date().toISOString()) => {
    const memory = read(storage, now).memory;
    memory.visitor.welcomeDismissedAt = now;
    write(storage, memory);
    return memory;
  };
  const rememberSearch = (storage, query, details = {}, now = new Date().toISOString()) => {
    const cleanQuery = text(query);
    if (!cleanQuery) return read(storage, now).memory;
    const memory = read(storage, now).memory;
    const entry = {
      query: cleanQuery,
      fingerprint: text(details.fingerprint),
      at: now
    };
    memory.preferences.lastSearch = cleanQuery;
    memory.recentSearches = uniqueRecent(
      [entry, ...memory.recentSearches],
      (item) => item.query.toLowerCase(),
      MAX_RECENT_SEARCHES
    );
    write(storage, memory);
    return memory;
  };
  const rememberBuild = (storage, build = {}, now = new Date().toISOString()) => {
    const memory = read(storage, now).memory;
    const project = text(build.project, 80);
    const style = text(build.style, 80);
    const budget = text(build.budget, 40);
    if (project) {
      memory.preferences.lastBuild = project;
      memory.preferences.lastAnimal = [
        'crested_gecko', 'ball_python', 'bearded_dragon', 'dart_frogs', 'tarantula', 'isopods'
      ].includes(project) ? project : memory.preferences.lastAnimal;
      memory.recentProjects = uniqueRecent([{
        project,
        style: style || null,
        budget: budget || null,
        step: Math.min(5, Math.max(1, Number(build.step) || 1)),
        at: now
      }, ...memory.recentProjects], (item) => item.project, MAX_RECENT_PROJECTS);
    }
    if (style) memory.preferences.preferredStyle = style;
    if (budget) memory.preferences.preferredBudget = budget;
    write(storage, memory);
    return memory;
  };
  const rememberFamily = (storage, family, now = new Date().toISOString()) => {
    const normalized = text(family, 80).toLowerCase().replace(/[\s-]+/g, '_');
    const memory = read(storage, now).memory;
    if (!FAMILIES.includes(normalized)) return memory;
    memory.preferences.recentFamilies = uniqueRecent(
      [normalized, ...memory.preferences.recentFamilies],
      (item) => item,
      MAX_RECENT_FAMILIES
    );
    write(storage, memory);
    return memory;
  };

  const createHandoff = (experienceId, now = new Date().toISOString()) => {
    const experience = EXPERIENCES[experienceId];
    if (!experience) return null;
    return {
      version: 1,
      experienceId,
      createdAt: now,
      expiresAt: new Date(Date.parse(now) + (30 * 60 * 1000)).toISOString(),
      ...experience.handoff,
      cartMutationAllowed: false,
      inventoryAuthority: 'shopify_current'
    };
  };
  const storeHandoff = (storage, experienceId, now = new Date().toISOString()) => {
    const handoff = createHandoff(experienceId, now);
    if (!handoff) return false;
    try {
      storage?.setItem(HANDOFF_KEY, JSON.stringify(handoff));
      return true;
    } catch (_) {
      return false;
    }
  };
  const consumeHandoff = (storage, now = new Date().toISOString()) => {
    const handoff = parse(storage, HANDOFF_KEY);
    try { storage?.removeItem(HANDOFF_KEY); } catch (_) { /* optional storage */ }
    if (!handoff || handoff.version !== 1 || !Object.hasOwn(EXPERIENCES, handoff.experienceId)) return null;
    if (Date.parse(handoff.expiresAt || '') < Date.parse(now)) return null;
    return handoff;
  };
  const patchBuildState = (input = {}, handoff = null, createEmpty = () => ({})) => {
    if (!handoff) return { ...input };
    const state = handoff.reset ? createEmpty() : { ...input };
    if (handoff.project && !state.project) state.project = handoff.project;
    if (handoff.style && !state.style) state.style = handoff.style;
    return state;
  };

  const contextFor = (storage, surface = 'homepage') => {
    const memory = read(storage).memory;
    const experience = EXPERIENCES[memory.intent.experienceId] || null;
    return Object.freeze({
      version: 1,
      surface,
      experienceId: memory.intent.experienceId,
      contextLabel: experience?.contextLabel || null,
      searchPrompt: experience?.searchPrompt || null,
      lastAnimal: memory.preferences.lastAnimal,
      lastBuild: memory.preferences.lastBuild,
      lastSearch: memory.preferences.lastSearch,
      preferredStyle: memory.preferences.preferredStyle,
      preferredBudget: memory.preferences.preferredBudget,
      recentFamilies: [...memory.preferences.recentFamilies],
      localOnly: true,
      inventoryAuthority: 'shopify_current',
      compatibilityAuthority: 'not_granted'
    });
  };

  const labelProject = (value) => text(value, 80)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const setHidden = (element, hidden) => {
    if (element) element.hidden = Boolean(hidden);
  };
  const dispatch = (doc, name, detail = {}) => {
    const EventConstructor = doc.defaultView?.CustomEvent || globalThis.CustomEvent;
    if (EventConstructor) doc.dispatchEvent(new EventConstructor(name, { detail }));
  };
  const renderRecent = (list, entries, type) => {
    if (!list) return;
    list.replaceChildren(...entries.map((entry) => {
      const item = list.ownerDocument.createElement('li');
      if (type === 'search') {
        const link = list.ownerDocument.createElement('a');
        link.href = `${list.dataset.searchUrl || '/search'}?type=product&q=${encodeURIComponent(entry.query)}`;
        link.textContent = entry.query;
        item.append(link);
      } else {
        const link = list.ownerDocument.createElement('a');
        link.href = list.dataset.buildUrl || '/pages/build-studio';
        link.dataset.firstRunChoice = 'continue_build';
        link.textContent = labelProject(entry.project);
        const small = list.ownerDocument.createElement('small');
        small.textContent = [entry.style && labelProject(entry.style), entry.budget && (entry.budget === 'unlimited' ? 'Unlimited' : `$${entry.budget}`)]
          .filter(Boolean).join(' · ');
        item.append(link, small);
      }
      return item;
    }));
    list.hidden = entries.length === 0;
  };

  let initialized = false;
  const init = (doc, windowRef = globalThis) => {
    if (initialized || !doc) return null;
    initialized = true;
    const storage = windowRef.localStorage;
    const session = windowRef.sessionStorage;
    const bodyCartCount = Number(doc.body?.dataset.shopifyCartItems) || 0;
    const visit = startVisit(storage);
    const continuity = inspectContinuity(storage, bodyCartCount);
    const context = contextFor(storage);
    const page = doc.documentElement;
    page.dataset.corkopiaVisit = visit.firstVisit ? 'first' : 'returning';
    page.dataset.corkopiaExperience = context.experienceId || 'unselected';

    const welcome = doc.querySelector('[data-first-run]');
    if (welcome) {
      const firstPanel = welcome.querySelector('[data-first-run-first]');
      const returningPanel = welcome.querySelector('[data-first-run-returning]');
      const title = welcome.querySelector('[data-first-run-title]');
      const eyebrow = welcome.querySelector('[data-first-run-eyebrow]');
      const continueChoice = welcome.querySelector('[data-first-run-choice="continue_build"]');
      const resumeBuild = welcome.querySelector('[data-first-run-resume="build"]');
      const resumeCanvas = welcome.querySelector('[data-first-run-resume="canvas"]');
      const resumeSmartBox = welcome.querySelector('[data-first-run-resume="smart_box"]');
      const resumeCart = welcome.querySelector('[data-first-run-resume="cart"]');
      const hasContinuity = continuity.returningBuild || continuity.returningCart || continuity.recentSearches.length > 0;
      const showReturning = visit.returningVisitor && hasContinuity;

      setHidden(firstPanel, showReturning);
      setHidden(returningPanel, !showReturning);
      setHidden(continueChoice, !continuity.returningBuild);
      setHidden(resumeBuild, !continuity.returningBuild);
      setHidden(resumeCanvas, !continuity.returningCanvas);
      setHidden(resumeSmartBox, !continuity.returningSmartBox);
      setHidden(resumeCart, !continuity.returningCart);

      if (showReturning) {
        if (eyebrow) eyebrow.textContent = 'Welcome back';
        if (title) title.textContent = 'Ready to keep building?';
      } else if (visit.returningVisitor) {
        if (eyebrow) eyebrow.textContent = 'Start anywhere';
        if (title) title.textContent = 'What are you building today?';
      }
      if (context.contextLabel) {
        welcome.dataset.firstRunContext = context.experienceId;
        const contextNote = welcome.querySelector('[data-first-run-context-note]');
        if (contextNote) {
          contextNote.textContent = `Last direction: ${context.contextLabel}. Change it whenever you like.`;
          contextNote.hidden = false;
        }
      }
      renderRecent(
        welcome.querySelector('[data-first-run-recent-searches]'),
        continuity.recentSearches,
        'search'
      );
      renderRecent(
        welcome.querySelector('[data-first-run-recent-projects]'),
        continuity.recentProjects,
        'project'
      );
    }

    doc.querySelectorAll('[data-first-run-search-prompt]').forEach((input) => {
      if (context.searchPrompt) input.placeholder = context.searchPrompt;
    });
    const guidance = doc.querySelector('[data-smart-box-mount-guidance]');
    if (guidance && context.lastBuild) {
      guidance.textContent = `Continue ${labelProject(context.lastBuild)}`;
    }

    doc.addEventListener('click', (event) => {
      const dismiss = event.target.closest?.('[data-first-run-dismiss]');
      if (dismiss) {
        dismissWelcome(storage);
        const panel = dismiss.closest('[data-first-run-first]');
        if (panel) {
          panel.hidden = true;
          const returning = welcome?.querySelector('[data-first-run-returning]');
          if (returning) {
            returning.hidden = false;
            returning.querySelector('[data-returning-summary]')?.focus?.();
          }
        }
        dispatch(doc, 'corkopia:first-run', { action: 'dismissed', localOnly: true });
        return;
      }

      const smartBoxResume = event.target.closest?.('[data-first-run-resume="smart_box"], [data-first-run-resume="cart"]');
      if (smartBoxResume) {
        event.preventDefault();
        doc.querySelector('[data-review-build-open]')?.click();
        dispatch(doc, 'corkopia:first-run', { action: 'resume_smart_box', localOnly: true });
        return;
      }

      const choice = event.target.closest?.('[data-first-run-choice]');
      if (choice) {
        const experienceId = choice.dataset.firstRunChoice;
        const experience = EXPERIENCES[experienceId];
        if (!experience) return;
        rememberIntent(storage, experienceId);
        storeHandoff(session, experienceId);
        page.dataset.corkopiaExperience = experienceId;
        dispatch(doc, 'corkopia:first-run', {
          action: 'experience_selected',
          experienceId,
          destination: experience.destination,
          localOnly: true
        });
      }

      const card = event.target.closest?.('[data-cork-product-card]');
      const family = card?.dataset.normalizedFamily || card?.dataset.corkType;
      if (family) rememberFamily(storage, family);
    });

    dispatch(doc, 'corkopia:store-context-ready', context);
    return { visit, continuity, context };
  };

  return Object.freeze({
    STORAGE_KEY,
    HANDOFF_KEY,
    BUILD_KEY,
    SMART_BOX_KEY,
    SEARCH_HISTORY_KEY,
    FAMILIES,
    EXPERIENCES,
    createMemory,
    sanitizeMemory,
    read,
    write,
    startVisit,
    inspectContinuity,
    rememberIntent,
    dismissWelcome,
    rememberSearch,
    rememberBuild,
    rememberFamily,
    createHandoff,
    storeHandoff,
    consumeHandoff,
    patchBuildState,
    contextFor,
    init
  });
});
